import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { legalInfo } from '../content';
import Seo from '../components/Seo';

const Confidentialite = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Politique de confidentialité | Espace Agenda"
        description="Politique de confidentialité d'Espace Agenda : données collectées, finalités, conservation et vos droits RGPD (accès, rectification, suppression)."
      />
      <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8 lg:py-24">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
        </Link>

        <h1 className="text-4xl font-bold font-heading tracking-tight text-foreground mb-8">
          Politique de confidentialité
        </h1>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Introduction</h2>
            <p className="text-gray-700">
              La présente politique décrit comment {legalInfo.brandName} collecte, utilise et protège les données personnelles des visiteurs et prospects de son site web.
            </p>
            <p className="text-gray-700">
              Elle ne couvre pas les données que les praticiens clients d'{legalInfo.brandName} saisissent, dans le cadre de leur propre activité, sur leurs patients ou clients au sein de l'outil de réservation. Ce traitement relève d'un cadre contractuel distinct entre {legalInfo.brandName} et chaque praticien.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Responsable du traitement</h2>
            <p className="text-gray-700">
              <strong>{legalInfo.companyName}</strong><br />
              {legalInfo.legalForm}<br />
              Adresse : {legalInfo.address}<br />
              SIRET : {legalInfo.siret}<br />
              Email : {legalInfo.email}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Données collectées, finalités et durées de conservation</h2>

            <h3 className="text-xl font-semibold text-foreground mb-2">Formulaire de contact</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 mb-6">
              <li><strong>Données :</strong> nom, adresse email, numéro de téléphone (facultatif), objet, profession (facultatif), contenu de votre message.</li>
              <li><strong>Finalité :</strong> répondre à votre demande.</li>
              <li><strong>Base légale :</strong> mesures précontractuelles prises à votre demande et intérêt légitime à répondre aux sollicitations reçues.</li>
              <li><strong>Durée :</strong> 3 ans à compter du dernier échange.</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mb-2">Lettre d'information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 mb-6">
              <li><strong>Données :</strong> adresse email.</li>
              <li><strong>Finalité :</strong> vous adresser, avec votre consentement, une lettre d'information occasionnelle sur la gestion des rendez-vous en cabinet. Votre adresse ne sert qu'à cela.</li>
              <li><strong>Base légale :</strong> votre consentement. Vous pouvez vous désinscrire à tout moment.</li>
              <li><strong>Durée :</strong> jusqu'à votre désinscription, et au plus 3 ans après votre dernière interaction.</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mb-2">Mesure d'audience</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              <li><strong>Données :</strong> identifiant de navigateur déposé dans un cookie, pages consultées, durée de visite, type d'appareil et de navigateur, provenance (site référent), localisation approximative. Aucun enregistrement de votre navigation n'est réalisé.</li>
              <li><strong>Finalité :</strong> comprendre l'usage du site pour l'améliorer.</li>
              <li><strong>Base légale :</strong> votre consentement, recueilli par le bandeau cookies. Sans votre accord, aucune donnée de mesure d'audience n'est collectée.</li>
              <li><strong>Durée :</strong> cookies conservés 13 mois au maximum ; données d'audience conservées 14 mois au maximum.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Destinataires et sous-traitants</h2>
            <p className="text-gray-700">
              Vos données sont destinées à {legalInfo.brandName}. Elles ne sont ni vendues ni cédées. Elles sont traitées par les prestataires suivants, pour le compte d'{legalInfo.brandName} :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Hostinger International Ltd (Chypre)</strong> — hébergement du site et de ses données, envoi des emails issus du formulaire de contact.</li>
              <li><strong>Google Ireland Limited (Google Analytics)</strong> — mesure d'audience, après consentement uniquement. Les données peuvent être traitées aux États-Unis par Google LLC, qui adhère au cadre de protection des données UE–États-Unis. Ce transfert n'a lieu que si vous avez accepté les cookies de mesure d'audience.</li>
              <li><strong>Elfsight</strong> — affichage du bandeau cookies et conservation de votre choix.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Délégué à la protection des données</h2>
            <p className="text-gray-700">
              Aucun délégué à la protection des données n'est désigné. Le responsable du traitement exerce lui-même cette fonction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Vos droits</h2>
            <p className="text-gray-700">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> corriger vos données inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement :</strong> supprimer vos données dans certaines conditions</li>
              <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
              <li><strong>Droit à la portabilité :</strong> récupérer vos données dans un format structuré</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit de retirer votre consentement</strong> à tout moment, sans remettre en cause la licéité du traitement antérieur</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Pour exercer vos droits, écrivez à {legalInfo.email}.
            </p>
            <p className="text-gray-700">
              Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                CNIL (www.cnil.fr)
              </a>.
            </p>
          </section>

          <section className="pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Dernière mise à jour : {legalInfo.lastUpdate}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Confidentialite;
